import * as React from 'react';
import { cx } from '../utils';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export type SkeletonShape = 'rect' | 'text' | 'circle';

/* Bounded rest-prop allow-list (codex Card r1 lesson). */
type AllowedRest = Pick<React.HTMLAttributes<HTMLElement>, 'id' | 'title' | 'lang' | 'dir'> & {
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | boolean | number | undefined;
};
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface SkeletonProps extends StyledProps, UnsafeStyleProps, AllowedRest {
  /* Shape preset:
     - rect: arbitrary block (caller sets width/height via props or unsafe_style)
     - text: a single text-line placeholder (1em tall by default)
     - circle: avatar / icon placeholder (caller sets size) */
  shape?: SkeletonShape;
  /* Width and height accept number (px) or any CSS length string. */
  width?: number | string;
  height?: number | string;
  /* Lines: for shape="text", renders N skeleton lines (last line is
     ~60% width by default to look natural). Defaults to 1. */
  lines?: number;
  /* Accessible label. Default 'Loading content'. Pass a more specific
     label for context ('Loading transactions table'). */
  ariaLabel?: string;
  /* When true, omits role + aria-label — for skeletons inside a
     labeled parent that handles the loading semantics. */
  decorative?: boolean;
}

function dim(v: number | string | undefined): string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'number') return `${v}px`;
  return v;
}

/**
 * Content-shape loading placeholder. Renders a gray shimmer that
 * mirrors the shape of the content that will replace it. Use INSTEAD
 * of Spinner for content regions where preserving layout matters
 * (cards, tables, list rows).
 *
 * A11y model:
 * - default: role="status" + aria-label + aria-live="polite"
 * - decorative: aria-hidden=true + no role (for skeletons inside a
 *   labeled parent — e.g., a list view that already announces "Loading")
 *
 * For text shape with lines > 1, only ONE labeled role region is
 * emitted (the wrapper); the line elements are presentational.
 */
export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  {
    shape = 'rect',
    width,
    height,
    lines = 1,
    ariaLabel = 'Loading content',
    decorative,
    className,
    unsafe_style,
    ...rest
  },
  ref,
) {
  const restProps = pickAllowed(
    rest as Record<string, unknown>,
  ) as React.HTMLAttributes<HTMLDivElement>;

  /* Respect adopter aria-label via rest pass-through, same pattern
     as Spinner r1. */
  const restAriaLabel = (restProps as { 'aria-label'?: string })['aria-label'];

  const ariaProps = decorative
    ? ({ 'aria-hidden': true as const } as const)
    : ({
        role: 'status' as const,
        'aria-label': restAriaLabel ?? ariaLabel,
        'aria-live': 'polite' as const,
      } as const);

  const inlineSize = dim(width);
  const blockSize = dim(height);
  const styleBase: React.CSSProperties = {
    ...(inlineSize ? { inlineSize } : null),
    ...(blockSize ? { blockSize } : null),
    ...unsafe_style,
  };

  /* Text mode with multi-line: render a wrapper + N inner lines.
     The wrapper holds the role/label; the lines are pure visual. */
  if (shape === 'text' && lines > 1) {
    return (
      <div
        ref={ref}
        {...restProps}
        {...ariaProps}
        className={cx('j-skeleton-stack', className)}
        style={styleBase}
      >
        {Array.from({ length: lines }, (_, i) => (
          <span
            key={i}
            className={cx('j-skeleton', 'j-skeleton-text')}
            aria-hidden="true"
            style={i === lines - 1 ? { inlineSize: '60%' } : undefined}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      {...restProps}
      {...ariaProps}
      className={cx('j-skeleton', `j-skeleton-${shape}`, className)}
      style={styleBase}
    />
  );
});
